import { useEffect, useState } from 'react';
import { Smoke, Dust } from '../components/Atmosphere.js';
import type { ClientMsg, Lang, PlayerView } from '../../../shared/src/types.js';

interface Props {
  t: Lang;
  view: PlayerView;
  send: (msg: ClientMsg) => void;
}

export function PlayerLobby({ t, view, send }: Props) {
  // One-shot 'YOU JOINED' confirmation banner — gives the player immediate
  // feedback that the join actually landed (the lobby alone is too quiet).
  const [showJoined, setShowJoined] = useState(true);
  useEffect(() => {
    const id = setTimeout(() => setShowJoined(false), 2400);
    return () => clearTimeout(id);
  }, []);

  // The lobby is dead time — fill it with a generic, dismissible how-to-play
  // primer (night→day→vote loop + Town-vs-Mafia goal). Kept open by default so
  // first-timers see it; never leaks per-player roles (lobby has none).
  const [howToOpen, setHowToOpen] = useState(true);

  const onLeave = () => {
    const msg = t === 'ru'
      ? 'Покинуть лобби?'
      : 'Покинути лобі?';
    if (window.confirm(msg)) send({ kind: 'player:leave' });
  };

  return (
    <div className="scr">
      <div className="bg-noir" />
      <Smoke />
      <Dust count={14} />
      <div className="vig vignette-pulse" />

      <button
        className="exit-room-btn mono"
        onClick={onLeave}
        title={t === 'ru' ? 'Покинуть лобби' : 'Покинути лобі'}
      >
        ✕ {t === 'ru' ? 'ВЫЙТИ' : 'ВИЙТИ'}
      </button>

      {showJoined && (
        <div className="joined-banner">
          <span className="display">{t === 'ru' ? 'ВЫ В ИГРЕ' : 'ВИ В ГРІ'}</span>
          <span className="mono">{view.me.name}</span>
        </div>
      )}

      <div className="lobby-head">
        <div className="phase-pill phase-night">
          <span className="moon-dot" />
          <span className="sub">{t === 'ru' ? 'ОЖИДАНИЕ ВЕДУЩЕГО' : 'ОЧІКУВАННЯ ВЕДУЧОГО'}</span>
        </div>
        <div className="lobby-code display">
          {view.room.code}
          <small>{t === 'ru' ? 'КОД КОМНАТЫ' : 'КОД КІМНАТИ'}</small>
        </div>
      </div>

      <div className="player-list">
        <div className="sub" style={{ color: 'var(--bone-dim)', fontSize: 10, padding: '0 4px' }}>
          {t === 'ru' ? 'ИГРОКИ' : 'ГРАВЦІ'} · {view.room.players.length}
        </div>
        {view.room.players.map((p, i) => {
          const isMe = p.id === view.me.id;
          return (
            <div
              key={p.id}
              className={`player-row ${p.connected ? '' : 'disconnected'} ${isMe ? 'me' : ''}`}
            >
              <span className="mono" style={{ color: 'var(--smoke)', fontSize: 11 }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span>{p.name}</span>
              {isMe && (
                <span className="role-tag" style={{ color: 'var(--toxic-green)' }}>
                  {t === 'ru' ? 'ВЫ' : 'ВИ'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div
        className="reveal-hint mono"
        style={{
          margin: '12px 22px 0',
          padding: 0,
          textAlign: 'left',
          letterSpacing: 'normal',
          flex: 'none',
          zIndex: 5,
          position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={() => setHowToOpen((v) => !v)}
          aria-expanded={howToOpen}
          className="sub mono"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            background: 'transparent',
            border: 'none',
            color: 'var(--bone-dim)',
            letterSpacing: '.28em',
            fontSize: 11,
            padding: '12px 14px',
            cursor: 'pointer',
          }}
        >
          <span>{t === 'ru' ? 'КАК ИГРАТЬ' : 'ЯК ГРАТИ'}</span>
          <span aria-hidden="true" style={{ color: 'var(--smoke)' }}>{howToOpen ? '▾' : '▸'}</span>
        </button>
        {howToOpen && (
          <div
            style={{
              padding: '0 14px 14px',
              color: 'var(--bone)',
              letterSpacing: 'normal',
              fontSize: 12,
              lineHeight: 1.55,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ color: 'var(--bone-dim)' }}>
              {t === 'ru'
                ? 'Ваша тайная роль придёт в начале игры. Никому её не показывайте.'
                : 'Ваша таємна роль прийде на початку гри. Нікому її не показуйте.'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{ color: 'var(--toxic-green)' }}>1.</span>
              <span>
                {t === 'ru'
                  ? 'НОЧЬ — город спит, мафия и особые роли тайно действуют.'
                  : 'НІЧ — місто спить, мафія та особливі ролі таємно діють.'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{ color: 'var(--toxic-green)' }}>2.</span>
              <span>
                {t === 'ru'
                  ? 'ДЕНЬ — все обсуждают, что случилось ночью, и ищут мафию.'
                  : 'ДЕНЬ — усі обговорюють, що сталося вночі, і шукають мафію.'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{ color: 'var(--toxic-green)' }}>3.</span>
              <span>
                {t === 'ru'
                  ? 'ГОЛОСОВАНИЕ — стол выбирает, кого изгнать. Цикл повторяется.'
                  : 'ГОЛОСУВАННЯ — стіл обирає, кого вигнати. Цикл повторюється.'}
              </span>
            </div>
            <div style={{ color: 'var(--bone-dim)', marginTop: 2 }}>
              {t === 'ru'
                ? 'ЦЕЛЬ: мирные жители — вычислить и изгнать всю мафию; мафия — остаться в большинстве.'
                : 'МЕТА: мирні жителі — вирахувати й вигнати всю мафію; мафія — залишитися в більшості.'}
            </div>
          </div>
        )}
      </div>

      <div className="vote-foot" style={{ gridTemplateColumns: '1fr' }}>
        <div className="reveal-hint mono" style={{ flex: 1, textAlign: 'center', padding: 14 }}>
          {t === 'ru' ? 'ВЕДУЩИЙ НАЧНЁТ ИГРУ' : 'ВЕДУЧИЙ ПОЧНЕ ГРУ'}
        </div>
      </div>
    </div>
  );
}
